import { useMemo } from 'react';
import {
	SERVICE_DESK_PROJECT,
	SOFTWARE_PROJECT,
	CORE_PROJECT,
	toProjectType,
	type ProjectType,
	PRODUCT_DISCOVERY_PROJECT,
	projectTypeToCanonicalId,
	type CanonicalId,
} from '@jira/platform__project-types/src/project-types.tsx';
import {
	STANDARD_EDITION,
	getEditionForProject,
} from '@jira/platform__application-editions/src/edition.tsx';
import type { JiraSubProducts } from '@atlassian/auto-upgrade-not-a-trial';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';

import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';

export type ProductAndEdition = {
	product?: JiraSubProducts;
	productKey?: CanonicalId;
	projectType?: ProjectType;
	isStandardEdition: boolean;
};

const getPlatformProduct = (p?: ProjectType): JiraSubProducts | undefined => {
	if (p === SOFTWARE_PROJECT) return 'jira';
	if (p === PRODUCT_DISCOVERY_PROJECT) return 'jira-product-discovery';
	if (p === SERVICE_DESK_PROJECT) return 'jira-service-management';
	if (p === CORE_PROJECT) return 'jira';
};

export const useProductAndEdition = (): ProductAndEdition => {
	const appEditions = useAppEditions();

	const { loading, data: project } = useProject();

	const projectType = useMemo(() => {
		if (loading) return;

		const type = toProjectType(project?.type);

		if (type) return type;
	}, [loading, project?.type]);

	const localEdition = useMemo(() => {
		if (!projectType) return;

		return getEditionForProject(projectType, appEditions);
	}, [appEditions, projectType]);

	const isStandardEdition = localEdition === STANDARD_EDITION;

	const productKey = projectType && projectTypeToCanonicalId(projectType);
	const product = getPlatformProduct(projectType);

	return useMemo(
		() => ({
			product,
			productKey,
			projectType,
			isStandardEdition,
		}),
		[product, productKey, projectType, isStandardEdition],
	);
};
